/**
 * Multi-layer parallax space background.
 * Composes 5 layers at different depths and parallax factors to create
 * depth perception and speed feedback as the camera follows the ship.
 *
 * All particle layers use seeded PRNG so every player in the same
 * solar system sees identical star/dust/debris placement.
 *
 * Layer order (deepest to nearest):
 *   Volumetric nebula  (0.03) - fullscreen clip-space quad, fractal galaxy shader
 *   Dust/rock          (0.15) - Y -30 - dim brown/gray particles
 *   Background stars   (0.30) - Y -20 - classic starfield
 *   Near bright stars  (0.65) - Y -10 - strong speed cue
 *   Debris             (0.85) - Y -5  - drifting rocks/grit, closest parallax
 */

import { useMemo } from 'react';
import { VolumetricNebula } from './VolumetricNebula.js';
import { ParallaxStarfield } from './ParallaxStarfield.js';
import { useGameStore } from '../../stores/gameStore.js';
import { TICK_DELTA } from '@warmancer/shared';

// Parallax factors exported for tests
export const NEBULA_PARALLAX = 0.03;
export const DUST_PARALLAX = 0.15;
export const STARS_PARALLAX = 0.30;
export const NEAR_STARS_PARALLAX = 0.65;
export const DEBRIS_PARALLAX = 0.85;

// Stable array references — inline arrays cause useMemo regeneration on every render
const DUST_COLORS = ['#b09070', '#c0a888', '#998872', '#a89880'] as const;
const STAR_COLORS = ['#ffffff', '#ffffff', '#aaccff', '#ffe8a0', '#ffccaa'] as const;
const NEAR_STAR_COLORS = ['#ffffff', '#aaccff', '#ffe8a0'] as const;
const DEBRIS_COLORS = ['#887868', '#9a8a78', '#7a6e62', '#a09080', '#8b7d70'] as const;

/**
 * Derive deterministic seeds from worldSetup so all players
 * in the same solar system see identical particle layouts.
 * Each layer gets a unique offset to avoid correlated patterns.
 * When masterSeed is available (from ScenerySetup), use it directly
 * for guaranteed cross-client consistency.
 */
function deriveSeeds(planet: { mass: number; radius: number }, moon: { orbitSpeed: number }, masterSeed?: number) {
  // When masterSeed is available, use it directly — guaranteed identical across clients
  const base = masterSeed !== undefined
    ? masterSeed
    : Math.floor(Math.abs(planet.mass * 137 + planet.radius * 311 + moon.orbitSpeed * 719));
  return {
    dust: base ^ 0xDEAD0001,
    stars: base ^ 0xBEEF0002,
    nearStars: base ^ 0xCAFE0003,
    debris: base ^ 0xFACE0004,
  };
}

export function SpaceBackground() {
  const worldSetup = useGameStore((s) => s.worldSetup);
  const masterSeed = useGameStore((s) => s.scenerySetup?.masterSeed);
  const serverTick = useGameStore((s) => s.serverTick);
  const serverTimeBase = serverTick > 0 ? serverTick * TICK_DELTA : undefined;

  const seeds = useMemo(() => {
    if (!worldSetup) return { dust: 42, stars: 137, nearStars: 311, debris: 719 };
    return deriveSeeds(worldSetup.planet, worldSetup.moon, masterSeed);
  }, [worldSetup, masterSeed]);

  return (
    <group>
      {/* Layer 1: Volumetric nebula (deepest, barely moves) */}
      <VolumetricNebula />

      {/* Layer 2: Dust/rock particles */}
      <ParallaxStarfield
        count={600}
        parallaxFactor={DUST_PARALLAX}
        yDepth={-30}
        sizeRange={[4.0, 8.0]}
        colors={DUST_COLORS}
        spread={200}
        seed={seeds.dust}
        rocky
      />

      {/* Layer 3: Background stars (classic starfield) */}
      <ParallaxStarfield
        count={2000}
        parallaxFactor={STARS_PARALLAX}
        yDepth={-20}
        sizeRange={[2.5, 5.0]}
        colors={STAR_COLORS}
        spread={250}
        seed={seeds.stars}
      />

      {/* Layer 4: Near bright stars (strongest speed cue) */}
      <ParallaxStarfield
        count={120}
        parallaxFactor={NEAR_STARS_PARALLAX}
        yDepth={-10}
        sizeRange={[4.0, 8.0]}
        colors={NEAR_STAR_COLORS}
        spread={200}
        seed={seeds.nearStars}
      />

      {/* Layer 5: Drifting debris — rocks, dust, grit for life and texture */}
      <ParallaxStarfield
        count={300}
        parallaxFactor={DEBRIS_PARALLAX}
        yDepth={-5}
        sizeRange={[3.5, 9.0]}
        colors={DEBRIS_COLORS}
        spread={150}
        seed={seeds.debris}
        driftSpeed={[0.3, 1.2]}
        rocky
        serverTimeBase={serverTimeBase}
      />
    </group>
  );
}
